<template>
  <section id="screenshots" class="section-padding bg-gray-50">
    <div class="container-custom">
      <!-- Section Header -->
      <div class="text-center mb-16">
        <div class="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-800 text-sm font-medium mb-6">
          <i class="fas fa-images mr-2"></i>
          See It In Action
        </div>
        <h2 class="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
          Beautiful Interface,
          <span class="gradient-text">Powerful Functionality</span>
        </h2>
        <p class="text-xl text-gray-600 max-w-3xl mx-auto">
          Experience the intuitive design and powerful features that make Task Manager Pro 
          the perfect solution for your project management needs.
        </p>
      </div>
      
      <!-- Screenshots Grid -->
      <div class="grid lg:grid-cols-2 gap-12 items-center">
        <!-- Left Side - Screenshot 1 -->
        <div class="space-y-8">
          <div class="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div class="bg-gray-100 px-6 py-4 flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="w-3 h-3 bg-red-400 rounded-full"></div>
                <div class="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div class="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
              <span class="text-sm text-gray-600 font-medium">Kanban Board</span>
            </div>
            <div class="bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg flex items-center justify-center">
              <img src="/kanban-board.jpg" alt="Kanban board" />
            </div>
          </div>
          
          <!-- Feature Highlights -->
          <div class="grid grid-cols-2 gap-4">
            <div class="bg-white rounded-xl p-4 shadow-lg">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <i class="fas fa-drag text-green-600"></i>
                </div>
                <span class="text-sm font-medium text-gray-900">Drag & Drop</span>
              </div>
            </div>
            <div class="bg-white rounded-xl p-4 shadow-lg">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i class="fas fa-users text-blue-600"></i>
                </div>
                <span class="text-sm font-medium text-gray-900">Team Collaboration</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Right Side - Screenshot 2 -->
        <div class="space-y-8">
          <div class="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div class="bg-gray-100 px-6 py-4 flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="w-3 h-3 bg-red-400 rounded-full"></div>
                <div class="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div class="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
              <span class="text-sm text-gray-600 font-medium">Task Details</span>
            </div>
            <div class="bg-gradient-to-r from-green-100 to-blue-100 rounded-lg h-80 flex items-center justify-center">
              <img src="/task-details.jpg" alt="Task Details" />
            </div>
          </div>
          
          <!-- Feature Highlights -->
          <div class="grid grid-cols-2 gap-4">
            <div class="bg-white rounded-xl p-4 shadow-lg">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <i class="fas fa-clock text-purple-600"></i>
                </div>
                <span class="text-sm font-medium text-gray-900">Time Tracking</span>
              </div>
            </div>
            <div class="bg-white rounded-xl p-4 shadow-lg">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <i class="fas fa-chart-bar text-orange-600"></i>
                </div>
                <span class="text-sm font-medium text-gray-900">Analytics</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Additional Screenshots -->
      <div class="mt-16">
        <h3 class="text-2xl font-bold text-gray-900 mb-8 text-center">More Screenshots</h3>
        <div class="grid md:grid-cols-3 gap-8">
          <!-- Screenshot 3 -->
          <div class="bg-white rounded-xl shadow-lg overflow-hidden">
            <div class="bg-gray-100 px-4 py-3 flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <div class="w-2 h-2 bg-red-400 rounded-full"></div>
                <div class="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <div class="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <span class="text-xs text-gray-600">Dashboard</span>
            </div>
            <div class="p-4">
              <div class="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-lg h-48 flex items-center justify-center">
                <img src="/dashboard.jpg" alt="Dashboard" />
              </div>
            </div>
          </div>
          
          <!-- Screenshot 4 -->
          <div class="bg-white rounded-xl shadow-lg overflow-hidden">
            <div class="bg-gray-100 px-4 py-3 flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <div class="w-2 h-2 bg-red-400 rounded-full"></div>
                <div class="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <div class="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <span class="text-xs text-gray-600">Workflows</span>
            </div>
            <div class="p-4">
              <div class="bg-gradient-to-r from-green-100 to-blue-100 rounded-lg h-48 flex items-center justify-center">
                <img src="/workflows.jpg" alt="Workflows" />
              </div>
            </div>
          </div>
          
          <!-- Screenshot 5 -->
          <div class="bg-white rounded-xl shadow-lg overflow-hidden">
            <div class="bg-gray-100 px-4 py-3 flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <div class="w-2 h-2 bg-red-400 rounded-full"></div>
                <div class="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <div class="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <span class="text-xs text-gray-600">Analytics</span>
            </div>
            <div class="p-4">
              <div class="bg-gradient-to-r from-orange-100 to-red-100 rounded-lg h-48 flex items-center justify-center">
                <img src="/analytics.jpg" alt="Analytics" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ScreenshotsSection'
}
</script>


